import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import "ol/ol.css";
import Map from "ol/Map";
import TileLayer from "ol/layer/WebGLTile";
import GeoTIFF from "ol/source/GeoTIFF";
import View from "ol/View";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import { GeoJSON } from "ol/format";
import Control from "ol/control/Control";
import {
  getFilePreSignedUrl,
  getHarvestDates,
  getGraphData,
} from "../../../utils/api";
import { LoadingIcon } from "../../../components";
import { Fill, Stroke, Style } from "ol/style";
import {
  DatePicker,
  message,
  Slider,
  Switch,
  InputNumber,
  ColorPicker,
} from "antd";
import { BACKEND_DATE_FORMAT, UI_DATE_FORMAT } from "../../../consts";
import dayjs from "dayjs";
import { blobDate, blobSitePrefix } from "../../../utils/demoSiteBlob";

const dummyJson = {
  type: "FeatureCollection",
  features: [],
};

function addSuffixToFilename(url, suffix) {
  const dotIndex = url.lastIndexOf(".");
  if (dotIndex === -1) return url + "_" + suffix;
  const base = url.substring(0, dotIndex);
  const extension = url.substring(dotIndex);
  return base + "_" + suffix + extension;
}

function createCompassControl() {
  const element = document.createElement("div");
  element.className = "heatmap-compass ol-unselectable ol-control";

  element.innerHTML = `
    <div class="compass-rose" style="
      position: relative;
      width: 60px;
      height: 60px;
      border-radius: 50%;
      background: rgba(255,255,255,0.8);
      border: 1px solid #ccc;
      display: flex;
      align-items: center;
      justify-content: center;
      font-family: Arial, sans-serif;
    ">
      <div class="north" style="position: absolute; top: 3px; font-weight: bold; font-size: 12px;">N</div>
      <div class="east"  style="position: absolute; right: 3px;font-weight: bold; font-size: 12px;">E</div>
      <div class="south" style="position: absolute; bottom: 3px;font-weight: bold; font-size: 12px;">S</div>
      <div class="west"  style="position: absolute; left: 3px;font-weight: bold; font-size: 12px;">W</div>
    </div>
  `;
  return new Control({ element });
}

function parseColorStringToRGB(colorString) {
  if (!colorString) return [139, 69, 19];
  if (colorString.startsWith("#")) {
    const hex = colorString.replace("#", "");
    const bigint = parseInt(hex, 16);
    return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
  }
  if (colorString.startsWith("rgb")) {
    const nums = colorString
      .replace(/[^\d,]/g, "")
      .split(",")
      .map((n) => parseInt(n.trim(), 10));
    return [nums[0], nums[1], nums[2]];
  }
  return [139, 69, 19];
}

const defaultStops = [
  { threshold: 0.0, color: [139, 69, 19] },
  { threshold: 0.1, color: [160, 82, 45] },
  { threshold: 0.2, color: [205, 133, 63] },
  { threshold: 0.3, color: [222, 184, 135] },
  { threshold: 0.4, color: [238, 232, 170] },
  { threshold: 0.5, color: [189, 236, 182] },
  { threshold: 0.6, color: [156, 220, 144] },
  { threshold: 0.7, color: [118, 205, 118] },
  { threshold: 0.8, color: [85, 179, 85] },
  { threshold: 0.9, color: [34, 139, 34] },
  { threshold: 1.0, color: [0, 100, 0] },
];

/**
 * Build a new array of color stops that:
 *  - Skips all default stops < userThreshold
 *  - Inserts the user threshold with the user color
 *  - Keeps all stops above the user threshold
 */
function buildCustomStops(userThreshold, userColor) {
  const userRGB = parseColorStringToRGB(userColor);
  const filtered = defaultStops.filter((cs) => cs.threshold > userThreshold);
  return [{ threshold: userThreshold, color: userRGB }, ...filtered];
}

function interpolateColor(color1, color2, factor) {
  const result = color1.slice();
  for (let i = 0; i < 3; i++) {
    result[i] = Math.round(result[i] + factor * (color2[i] - color1[i]));
  }
  return result;
}

/**
 * Given a normalizedValue, figure out which two stops it falls between,
 * then interpolate the color.
 */
function getColorByValueWithGradient(normalizedValue, customStops) {
  if (!customStops?.length) return "rgba(255,0,0,0.6)";
  // If below the first threshold, just use first color
  if (normalizedValue <= customStops[0].threshold) {
    const [r, g, b] = customStops[0].color;
    return `rgba(${r},${g},${b},0.6)`;
  }
  // If above the last threshold, just use last color
  if (normalizedValue >= customStops.at(-1).threshold) {
    const [r, g, b] = customStops.at(-1).color;
    return `rgba(${r},${g},${b},0.6)`;
  }
  // Otherwise, find which two stops contain normalizedValue
  for (let i = 0; i < customStops.length - 1; i++) {
    const lower = customStops[i];
    const upper = customStops[i + 1];
    if (
      normalizedValue >= lower.threshold &&
      normalizedValue <= upper.threshold
    ) {
      const range = upper.threshold - lower.threshold || 1;
      const fraction = (normalizedValue - lower.threshold) / range;
      const rgb = interpolateColor(lower.color, upper.color, fraction);
      return `rgba(${rgb.join(",")},0.6)`;
    }
  }
  return "rgba(255,0,0,0.6)";
}

const HeatMapView = ({
  currentOrg,
  currentSite,
  siteMetadata,
  currentCrop,
  currentGreenhouse,
  currentPlot,
  isFullscreen = false,
  fullscreenAction = null,
  // dateList,
}) => {
  // Demo/clone sites read another site's blobs: the prefix is that site's name
  // (CLOUD_SITE_PREFIX) and demo dates map back to the source's dates per bay
  // (CLOUD_DATE_OFFSETS). Both keys are absent on ordinary sites.
  const cloudSitePrefix = blobSitePrefix(siteMetadata, currentSite);
  const toBlobDate = (d) => blobDate(siteMetadata, currentGreenhouse?.name, d);

  const [messageApi, contextHolder] = message.useMessage();
  const [cogFileUrl, setCogFileUrl] = useState(null);
  const [heatMapJson, setHeatMapJson] = useState(dummyJson);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [loadingMapData, setLoadingMapData] = useState(true);
  const [error, setError] = useState(null);

  const [dateList, setDateList] = useState([]);
  const [showHeatMap, setShowHeatMap] = useState(true);
  const [currentDate, setCurrentDate] = useState(null);
  const [validDates, setValidDates] = useState([]);

  const [gridSizes, setGridSizes] = useState([]);
  const [baseGridSizeValue, setBaseGridSizeValue] = useState(null);
  const [baseGridSizeUnit, setBaseGridSizeUnit] = useState(null);
  const [selectedGridSize, setSelectedGridSize] = useState(null);

  const [lowestThreshold, setLowestThreshold] = useState(0.0);
  const [lowestColor, setLowestColor] = useState("rgb(139,69,19)");

  // Crop stage state
  const [cropStage, setCropStage] = useState("unknown");
  const [thresholdRange, setThresholdRange] = useState({ min: 0.0, max: 1.0 });

  const [ndviFileName, setNdviFileName] = useState("rgb_cropped_cog.tif");

  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const tileLayerRef = useRef(null);
  const vectorLayerRef = useRef(null);

  const [tooltipOpen, setTooltipOpen] = useState(false);
  const [tooltipTitle, setTooltipTitle] = useState("");
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  const resetMap = () => {
    // 1) If there's an old map, remove target so OL can clean up
    if (mapInstanceRef.current) mapInstanceRef.current.setTarget(null);
    // 2) Clear out refs
    mapInstanceRef.current = null;
    tileLayerRef.current = null;
    vectorLayerRef.current = null;
    setTooltipOpen(false);
    setTooltipTitle("");
    setMousePos({ x: 0, y: 0 });
  };

  const getPreSignedUrl = async (url) => {
    try {
      const preSignedUrl = await getFilePreSignedUrl(
        currentOrg,
        currentSite.id,
        currentCrop.name,
        url,
        siteMetadata?.CLOUD_STORAGE_NAME || "azure",
        true,
        true,
      );
      return preSignedUrl?.data?.data?.url;
    } catch {
      setError("Error Fetching data !!!");
      return null;
    }
  };

  const convertCaretToSuperscript = (text) => {
    const map = {
      0: "⁰",
      1: "¹",
      2: "²",
      3: "³",
      4: "⁴",
      5: "⁵",
      6: "⁶",
      7: "⁷",
      8: "⁸",
      9: "⁹",
    };
    return text.replace(/\^(\d+)/g, (_, nums) =>
      nums
        .split("")
        .map((d) => map[d])
        .join(""),
    );
  };

  const fetchHeatMapJson = useCallback(
    async (heatMapURL) => {
      setLoadingMapData(true);
      try {
        const preSignedUrl = await getPreSignedUrl(heatMapURL);
        if (!preSignedUrl) {
          setError("No file heatmap json found !!!");
          return;
        }
        const res = await fetch(preSignedUrl);
        if (!res.ok) {
          setError("Failed to fetch heatmap JSON data");
          return;
        }
        const jsonData = await res.json();
        setHeatMapJson(jsonData);
      } catch {
        setError("Failed to fetch heatmap JSON data");
      } finally {
        setLoadingMapData(false);
      }
    },
    [getPreSignedUrl],
  );

  const fetchCogUrl = useCallback(
    async (cogURL, baseHeatMapJson) => {
      const url = await getPreSignedUrl(cogURL);
      if (!url) {
        setError("No NDVI found !!!");
        setLoadingMapData(false);
        return;
      }
      setCogFileUrl(url);
      const updatedHeatMapJson = addSuffixToFilename(
        baseHeatMapJson,
        selectedGridSize,
      );
      setError(null);
      fetchHeatMapJson(updatedHeatMapJson);
    },
    [getPreSignedUrl, selectedGridSize, fetchHeatMapJson],
  );

  useEffect(() => {
    if (siteMetadata) {
      const arr = siteMetadata["HEATMAP_NDVI_GRID_SIZES"];
      const baseVal = siteMetadata["HEATMAP_NDVI_BASE_GRID_SIZE"];
      const baseUnit = siteMetadata["HEATMAP_NDVI_BASE_GRID_SIZE_DEFAULT_UNIT"];
      const ndviName = siteMetadata["HEATMAP_NDVI_FILENAME"];
      if (ndviName) setNdviFileName(ndviName);
      if (Array.isArray(arr) && arr.length) {
        setGridSizes(arr);
        setBaseGridSizeValue(baseVal);
        setBaseGridSizeUnit(baseUnit);
        setSelectedGridSize(arr[0]);
        setLoadingMetadata(false);
      } else {
        setError("Grid Size metadata not found");
        setLoadingMetadata(false);
      }
    }
  }, [siteMetadata]);

  // 2. On selectedGridSize changes, fetch JSON (only once fetch COG if needed)
  useEffect(() => {
    if (!currentGreenhouse || !currentDate) return;
    setError(null);
    setLoadingMapData(true);
    resetMap();
    const cogURL = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/heatmap/${ndviFileName}`;
    const baseHeatMapJson = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/heatmap/grid.json`;
    fetchCogUrl(cogURL, baseHeatMapJson);
  }, [currentGreenhouse, currentDate, siteMetadata]);

  useEffect(() => {
    if (!dateList?.length || !currentGreenhouse || !currentPlot) {
      setValidDates([]);
      return;
    }

    let isMounted = true;
    setLoadingMapData(true);
    setError(null);

    const checkAllDates = async () => {
      try {
        const results = await Promise.all(
          dateList.map(async (d) => {
            const cogURL = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(d)}/${currentGreenhouse.name}/heatmap/${ndviFileName}`;
            const url = await getPreSignedUrl(cogURL);
            return url ? d : null;
          }),
        );
        const foundDates = results.filter(Boolean);
        if (isMounted) {
          if (foundDates.length) {
            setValidDates(foundDates);
            setCurrentDate(foundDates.at(-1));
            setError(null);
          } else {
            setValidDates([]);
            setError("No NDVI Heatmap found !!!");
          }
          setLoadingMapData(false);
        }
      } catch {
        if (isMounted) {
          setValidDates([]);
          setError("Error checking dates");
          setLoadingMapData(false);
        }
      }
    };
    checkAllDates();
    return () => {
      isMounted = false;
    };
  }, [dateList, currentGreenhouse, currentPlot, siteMetadata]);

  useEffect(() => {
    if (
      !selectedGridSize ||
      !currentDate ||
      !currentSite ||
      !currentCrop ||
      !currentGreenhouse
    )
      return;
    const baseHeatMapJson = `${cloudSitePrefix}/${currentCrop.name}/${toBlobDate(currentDate)}/${currentGreenhouse.name}/heatmap/grid.json`;
    const updatedHeatMapJson = addSuffixToFilename(
      baseHeatMapJson,
      selectedGridSize,
    );
    fetchHeatMapJson(updatedHeatMapJson);
  }, [
    selectedGridSize,
    currentDate,
    currentSite,
    siteMetadata,
    currentCrop,
    currentGreenhouse,
  ]);

  // 3. Create the map once we have the COG
  useEffect(() => {
    if (!cogFileUrl || !mapRef.current) return;
    // Create the tile layer from the COG
    const source = new GeoTIFF({ sources: [{ url: cogFileUrl }] });
    tileLayerRef.current = new TileLayer({ source });

    // Create the vector layer (NDVI grid)
    vectorLayerRef.current = new VectorLayer({
      source: new VectorSource(),
      style: (feature) => {
        const value = feature.get("normalizedValue") || 0;
        const stops = buildCustomStops(lowestThreshold, lowestColor);
        const fillColor = getColorByValueWithGradient(value, stops);
        return new Style({
          fill: new Fill({ color: fillColor }),
          stroke: new Stroke({ color: "#fff", width: 1 }),
        });
      },
    });

    // Create the map instance
    const map = new Map({
      target: mapRef.current,
      layers: [tileLayerRef.current, vectorLayerRef.current],
      view: new View({ center: [0, 0], zoom: 2 }),
    });
    mapInstanceRef.current = map;
    map.addControl(createCompassControl());
    // Center/zoom the map from COG metadata
    source.getView().then((v) => {
      if (v) {
        map.setView(new View(v));
      }
      setTimeout(() => {
        map.updateSize();
      }, 0);
    });
    // Hide tooltip on mouse leave
    const container = map.getTargetElement();
    container?.addEventListener("mouseleave", () => setTooltipOpen(false));
  }, [cogFileUrl]);

  // 4. Update the vector layer data whenever heatMapJson changes
  useEffect(() => {
    if (!mapInstanceRef.current || !vectorLayerRef.current) return;
    if (!heatMapJson.features?.length) return;
    const vals = heatMapJson.features.map((f) => f.properties.value);
    const maxVal = Math.max(...vals);
    const minVal = Math.min(...vals);
    const range = maxVal - minVal || 1;
    const normalize = (v) => (v - minVal) / range;
    const normData = {
      ...heatMapJson,
      features: heatMapJson.features.map((f) => ({
        ...f,
        properties: {
          ...f.properties,
          normalizedValue: normalize(f.properties.value),
        },
      })),
    };
    const map = mapInstanceRef.current;
    const handlePointerMove = (e) => {
      if (e.dragging) {
        setTooltipOpen(false);
        return;
      }
      const feat = map.forEachFeatureAtPixel(e.pixel, (f) => f);
      if (feat) {
        const rawVal = Number(feat.get("value"));
        const unit = feat.get("unit") || baseGridSizeUnit || "";
        if (!isNaN(rawVal)) {
          const raw = rawVal.toFixed(1);
          const pct = ((rawVal / maxVal) * 100).toFixed(1);
          setTooltipTitle(
            `Value: ${raw} ${convertCaretToSuperscript(unit)}<br/>Relative Percentage: ${pct}%`,
          );
          setTooltipOpen(true);
          setMousePos({ x: e.pixel[0] + 10, y: e.pixel[1] + 10 });
        } else setTooltipOpen(false);
      } else setTooltipOpen(false);
    };

    // Remove existing listener before adding a new one
    map.un("pointermove", handlePointerMove);
    map.on("pointermove", handlePointerMove);
    const newFeatures = new GeoJSON().readFeatures(normData, {
      featureProjection: "EPSG:4326",
    });
    const vectorSrc = new VectorSource({ features: newFeatures });
    vectorLayerRef.current.setSource(vectorSrc);

    // Fit view to new data
    const extent = vectorSrc.getExtent();
    if (extent && extent[0] !== Infinity) {
      map.getView().fit(extent, {
        padding: isFullscreen ? [20, 20, 20, 20] : [50, 50, 50, 50],
      });
    }
    return () => map.un("pointermove", handlePointerMove);
  }, [heatMapJson, isFullscreen, cogFileUrl]);

  // 5. Toggle NDVI grid visibility
  useEffect(() => {
    if (vectorLayerRef.current) vectorLayerRef.current.setVisible(showHeatMap);
  }, [showHeatMap]);

  // Ensure lowestThreshold is within valid range when thresholdRange changes
  useEffect(() => {
    if (lowestThreshold < thresholdRange.min) {
      setLowestThreshold(thresholdRange.min);
    } else if (lowestThreshold > thresholdRange.max) {
      setLowestThreshold(thresholdRange.max);
    }
  }, [thresholdRange, lowestThreshold]);

  useEffect(() => {
    if (!vectorLayerRef.current) return;
    vectorLayerRef.current.setStyle((f) => {
      const val = f.get("normalizedValue") || 0;
      const stops = buildCustomStops(lowestThreshold, lowestColor);
      const fill = getColorByValueWithGradient(val, stops);
      return new Style({
        fill: new Fill({ color: fill }),
        stroke: new Stroke({ color: "#fff", width: 1 }),
      });
    });
    vectorLayerRef.current.getSource()?.changed();
  }, [lowestThreshold, lowestColor]);

  const handleDateChange = (date) => {
    if (date) {
      const formattedDate = date.format(BACKEND_DATE_FORMAT);
      setCurrentDate(formattedDate);
      setLoadingMapData(true);
      setError(null);

      // Update crop stage when date changes
      determineCropStage(formattedDate);
    }
  };

  const fetchHarvestDates = async (orgId, siteId, cropId) => {
    try {
      const res = await getHarvestDates(orgId, siteId, cropId);
      const dates = res.data?.data?.map((d) => d.date);
      setDateList(dates);
    } catch {
      // no-op
    }
  };

  useEffect(() => {
    if (currentSite && currentCrop && currentOrg)
      fetchHarvestDates(currentOrg, currentSite.id, currentCrop.id);
  }, [currentSite, currentCrop, currentOrg]);

  const determineCropStage = useCallback(
    async (date) => {
      if (
        !date ||
        !currentOrg ||
        !currentSite ||
        !currentCrop ||
        !currentGreenhouse
      ) {
        setCropStage("unknown");
        setThresholdRange({ min: 0.0, max: 1.0 });
        return;
      }

      try {
        const graphRes = await getGraphData(
          currentOrg,
          currentSite.id,
          currentCrop.id,
          null,
          date,
          currentGreenhouse.id,
        );

        const greenhouseData = graphRes?.data?.data || {};

        const minIdealHarvestLeafSize =
          currentSite?.metadata?.[currentCrop.name]
            ?.MIN_IDEAL_HARVEST_LEAF_SIZE || 7.5;

        let dataPointCount = 0;
        let hasMatureSize = false;
        let hasMidSize = false;

        Object.values(greenhouseData).forEach((plotData) => {
          if (Array.isArray(plotData)) {
            plotData.forEach((dateData) => {
              if (dateData.harvest_date === date && dateData.data) {
                const leafSize =
                  dateData.data.percentiles?.["80_95"]?.average_length_cm ||
                  dateData.data.average_length_cm ||
                  0;
                if (leafSize > 0) {
                  dataPointCount++;
                  if (leafSize >= minIdealHarvestLeafSize) {
                    hasMatureSize = true;
                  } else if (leafSize >= 0.85 * minIdealHarvestLeafSize) {
                    hasMidSize = true;
                  }
                }
              }
            });
          }
        });

        if (hasMatureSize) {
          // Mature stage (ready for harvest)
          setCropStage("Ready to Harvest");
          setThresholdRange({ min: 0.7, max: 0.9 });
        } else if (hasMidSize) {
          // Mid stage
          setCropStage("Mid Stage");
          setThresholdRange({ min: 0.6, max: 0.75 });
        } else if (dataPointCount > 0) {
          // Early stage (only if we have data)
          setCropStage("Early Stage");
          setThresholdRange({ min: 0.5, max: 0.65 });
        }

        if (dataPointCount === 0) {
          console.warn(
            "No leaf size data available for the selected date, using fallback method",
          );
          setCropStage("N/A");
          setThresholdRange({ min: 0, max: 1 });
        }
      } catch (error) {
        console.error("Error determining crop stage:", error);
        setCropStage("unknown");
        setThresholdRange({ min: 0.0, max: 1.0 });
      }
    },
    [currentOrg, currentSite, currentCrop, currentGreenhouse],
  );

  useEffect(() => {
    if (!currentDate) return;
    if (dateList.length && !dateList.includes(currentDate)) {
      setError(null);
      const sorted = [...dateList].sort((a, b) => new Date(b) - new Date(a));
      setCurrentDate(sorted[0]);
    }

    // Determine crop stage when date changes
    determineCropStage(currentDate);
  }, [dateList, currentDate, determineCropStage]);

  const disabledDate = (cur) =>
    cur && !validDates.map((d) => dayjs(d)).some((d) => d.isSame(cur, "day"));

  const handleToggleHeatMap = (c) => setShowHeatMap(c);

  const parsedSizes = useMemo(
    () => gridSizes.map((g) => parseFloat(g)),
    [gridSizes],
  );
  const isConsecutive = useMemo(
    () => parsedSizes.every((v, i, arr) => !i || v === arr[i - 1] + 1),
    [parsedSizes],
  );
  const currentSliderValue = useMemo(
    () => (selectedGridSize ? parseFloat(selectedGridSize) : parsedSizes[0]),
    [selectedGridSize, parsedSizes],
  );
  const handleSliderChange = (v) => {
    const idx = parsedSizes.indexOf(v);
    if (idx !== -1) setSelectedGridSize(gridSizes[idx]);
  };
  let step, marks;
  if (isConsecutive) step = 1;
  else {
    step = null;
    marks = {};
    for (const ps of parsedSizes) marks[ps] = ps.toString();
  }
  const formatGridSize = (size, base, unit) => {
    const mult = parseFloat(size.replace("x", ""));
    const calc = mult * base;
    return `${size} (${calc} ${unit})`;
  };

  return (
    <div
      className={`heat-map w-full rounded-md relative ${
        isFullscreen
          ? "mb-0 flex h-full min-h-0 flex-col p-2 sm:p-1"
          : "mb-5 p-4 shadow-md sm:p-1"
      }`}
    >
      <h2 className={`font-bold ${isFullscreen ? "mb-2 text-lg" : "mb-3 text-lg"}`}>
        Heat Map
      </h2>
      {contextHolder}

      <div
        className={`z-50 ${
          isFullscreen
            ? "mb-3 flex w-full flex-wrap items-center justify-end gap-3"
            : "absolute right-2 top-[2rem] mb-4 flex flex-row items-center space-y-0 space-x-4 sm:flex-col sm:items-start sm:space-y-2 sm:space-x-0"
        }`}
      >
        {!error && (
          <>
            <div className="flex flex-row sm:flex-col items-start sm:items-center gap-2 sm:space-x-2 sm:justify-end">
              <div className="flex flex-col">
                <span className="font-bold whitespace-nowrap mb-1 sm:mb-0 sm:mr-2">
                  Lowest Threshold:
                </span>
                {cropStage !== "unknown" && (
                  <span className="text-xs text-darkerGray">
                    Current Stage:{" "}
                    {cropStage.charAt(0).toUpperCase() + cropStage.slice(1)}
                    {/*({thresholdRange.min} - {thresholdRange.max})*/}
                  </span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <InputNumber
                  min={0}
                  max={1}
                  step={0.01}
                  value={lowestThreshold}
                  onChange={(v) => {
                    if (typeof v === "number") {
                      if (v < thresholdRange.min) {
                        messageApi.open({
                          type: "warning",
                          content: `This is minimum threshold set for ${cropStage.charAt(0).toUpperCase() + cropStage.slice(1)} Stage (${thresholdRange.min} - ${thresholdRange.max})`,
                        });
                        setLowestThreshold(thresholdRange.min);
                      } else if (v > thresholdRange.max) {
                        messageApi.open({
                          type: "warning",
                          content: `This is maximum threshold set for ${cropStage.charAt(0).toUpperCase() + cropStage.slice(1)} Stage (${thresholdRange.min} - ${thresholdRange.max})`,
                        });
                        setLowestThreshold(thresholdRange.max);
                      } else {
                        setLowestThreshold(v);
                      }
                    }
                  }}
                />
                <ColorPicker
                  value={lowestColor}
                  onChange={(c) => setLowestColor(c.toRgbString())}
                />
              </div>
            </div>

            <div className="flex items-center space-x-2 justify-end">
              <span className="font-bold whitespace-nowrap mr-2">
                Grid Size:
              </span>
              <Slider
                value={currentSliderValue}
                min={parsedSizes[0]}
                max={parsedSizes.at(-1)}
                step={step}
                marks={marks}
                onChange={handleSliderChange}
                tooltip={{
                  formatter: (v) => {
                    const idx = parsedSizes.indexOf(v);
                    return idx !== -1
                      ? formatGridSize(
                          gridSizes[idx],
                          baseGridSizeValue,
                          baseGridSizeUnit,
                        )
                      : v;
                  },
                }}
                className="flex-1 min-w-[150px] max-w-[300px]"
                disabled={loadingMetadata || loadingMapData || !showHeatMap}
              />
            </div>

            <div className="flex items-center space-x-2 justify-end">
              <span className="font-bold">Show Heatmap:</span>
              <Switch
                checked={showHeatMap}
                onChange={handleToggleHeatMap}
                checkedChildren="On"
                unCheckedChildren="Off"
                disabled={loadingMetadata || loadingMapData}
              />
            </div>
          </>
        )}
        {currentDate && (
          <div className="flex items-center justify-end gap-2">
            <DatePicker
              value={dayjs(currentDate)}
              className={`max-w-32 min-w-[150px] ${
                isFullscreen ? "" : "sm:mt-4 sm:mb-6"
              }`}
              disabledDate={disabledDate}
              onChange={handleDateChange}
              format={UI_DATE_FORMAT}
              placeholder="Select Date"
              disabled={loadingMapData}
            />
            {fullscreenAction}
          </div>
        )}
        {!currentDate && fullscreenAction && (
          <div className="flex items-center justify-end">{fullscreenAction}</div>
        )}
      </div>

      <div
        className={isFullscreen ? "relative min-h-0 flex-1" : "mt-12"}
        style={{ position: "relative", minHeight: isFullscreen ? "0" : "70vh" }}
      >
        {error && (
          <div className="text-center text-dangerRed font-bold mt-10">
            {error}
          </div>
        )}

        {loadingMetadata && !error && (
          <div
            className="flex justify-center items-center"
            style={{ height: isFullscreen ? "100%" : "70vh" }}
          >
            <LoadingIcon />
          </div>
        )}

        {!loadingMetadata && !error && (
          <div
            ref={mapRef}
            className={`map w-full rounded-md ${
              isFullscreen ? "h-full min-h-0" : "sm:mt-56"
            }`}
            style={{
              height: isFullscreen ? "100%" : "70vh",
              position: "relative",
              zIndex: 0,
            }}
          >
            {loadingMapData && (
              <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-50 z-50">
                <LoadingIcon />
              </div>
            )}

            {tooltipOpen && (
              <div
                style={{
                  position: "absolute",
                  left: mousePos.x,
                  top: mousePos.y,
                  pointerEvents: "none",
                  zIndex: 9999,
                  background: "rgba(0, 0, 0, 0.7)",
                  color: "#fff",
                  padding: "5px",
                  borderRadius: "3px",
                  fontSize: "12px",
                  whiteSpace: "nowrap",
                }}
                dangerouslySetInnerHTML={{ __html: tooltipTitle }}
              />
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export { HeatMapView };
